import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { employeesService, plansService, timesService } from "services";
import { shouldIncludeSupervised } from "utils/utils";
import {
	alertEmployeeId,
	buildEmployeeSummary,
	employeeIdsMissingFromRegistrations,
	periodEmployeeMark,
	preserveSummaryStaff,
	periodTotalsFromMarks,
	scopeSummaryToUbication,
	spilledAlertRange,
	summarizeEmployees,
	widenSummaryRange,
} from "components/AttendanceByEmployee/aggregate";

const CACHE_MS = 30000;
export const SUMMARY_PAGE_SIZE = 25;
const REGISTRATION_PAGE_SIZE = 1000;
/**
 * El cálculo de alertas lee los fichajes de la página pedida y trata al resto
 * de la plantilla como si no hubiera fichado. La respuesta no viene paginada,
 * así que una sola llamada tiene que cubrir el periodo.
 */
const ALERT_PAGE_SIZE = 10000;
const listeners = new Set();

const emptyRaw = () => ({
	registrations: [],
	alerts: [],
	directory: [],
	assignments: [],
	plannedHours: [],
});

const state = {
	key: "",
	page: 0,
	hasNext: false,
	loading: false,
	error: null,
	summary: null,
	staff: [],
	periodMarks: new Map(),
	censusReady: false,
	exhausted: false,
	raw: emptyRaw(),
	loadedShiftIds: new Set(),
	stickyIds: new Set(),
	filters: null,
	coverage: null,
	at: 0,
};

let requestSerial = 0;
let activeController = null;
let decorationController = null;
let inflight = null;
let inflightSerial = 0;
let inflightKey = "";

function emit() {
	listeners.forEach((listener) => listener());
}

function subscribe(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function cacheKey(filters) {
	return [
		filters.startDate,
		filters.endDate,
		filters.employeeIds || "",
		filters.groupId || "",
		filters.ubicationId || "",
	].join("|");
}

function listFrom(response) {
	if (Array.isArray(response)) return response;
	if (Array.isArray(response?.data)) return response.data;
	return [];
}

function assertOk(response) {
	if (response && response.ok === false) {
		const error = new Error("load");
		error.name = "LoadError";
		throw error;
	}
}

export function summaryHasNextPage(
	staffLength,
	page,
	pageSize = SUMMARY_PAGE_SIZE,
	exhausted = false
) {
	const end = (page + 1) * pageSize;
	if (staffLength > end) return true;
	return !exhausted && staffLength >= end;
}

export function isLastPage(length, requestedSize, { untilEmpty = false } = {}) {
	if (length <= 0) return true;
	if (untilEmpty) return false;
	return length < requestedSize;
}

async function fetchPages(requestPage, pageSize, signal, { untilEmpty = false } = {}) {
	const all = [];
	const seen = new Set();

	for (let page = 0; page < 40; page += 1) {
		if (signal.aborted) {
			const error = new Error("aborted");
			error.name = "AbortError";
			throw error;
		}
		const response = await requestPage(page);
		assertOk(response);
		const data = listFrom(response);
		if (data.length === 0) break;
		const first = data[0]?.id || data[0]?.employee?.id || data[0]?.employee_id || "x";
		const last =
			data[data.length - 1]?.id ||
			data[data.length - 1]?.employee?.id ||
			data[data.length - 1]?.employee_id ||
			"y";
		const signature = `${data.length}:${first}:${last}`;
		if (page > 0 && seen.has(signature)) break;
		seen.add(signature);
		all.push(...data);
		if (isLastPage(data.length, pageSize, { untilEmpty })) break;
	}

	return all;
}

async function mapPool(items, limit, task) {
	const results = new Array(items.length);
	let next = 0;
	const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
		while (next < items.length) {
			const index = next;
			next += 1;
			results[index] = await task(items[index], index);
		}
	});
	await Promise.all(workers);
	return results;
}

function timeQuery(filters, page, pageSize) {
	const query = {
		start_from: filters.startDate,
		start_to: filters.endDate,
		page,
		page_size: pageSize,
		include_supervised: shouldIncludeSupervised(),
		sort: "+day",
	};
	if (filters.employeeIds) query.employee_ids = filters.employeeIds;
	if (filters.groupId) query.group_id = filters.groupId;
	return query;
}

function alertQuery(filters, pageSize) {
	return {
		...timeQuery(filters, 0, pageSize),
		sort: "+employee,+day",
	};
}

async function fetchPlannedHours(employeeIds, filters, signal) {
	if (!employeeIds.length) return [];
	const chunks = [];
	for (let index = 0; index < employeeIds.length; index += 25) {
		chunks.push(employeeIds.slice(index, index + 25));
	}
	const pages = await mapPool(chunks, 1, async (ids) => {
		for (let attempt = 0; attempt < 2; attempt += 1) {
			if (signal.aborted) {
				const error = new Error("aborted");
				error.name = "AbortError";
				throw error;
			}
			try {
				const response = await plansService.getPlannedHours({
					employee_ids: ids.join(","),
					start: filters.startDate,
					end: filters.endDate,
					signal,
				});
				if (response && response.ok !== false) return listFrom(response);
			} catch (error) {
				if (error?.name === "AbortError") throw error;
			}
		}
		return [];
	});
	return pages.flat();
}

async function fetchRegistrations(filters, signal) {
	return fetchPages(
		(page) => timesService.getAll(timeQuery(filters, page, REGISTRATION_PAGE_SIZE), signal),
		REGISTRATION_PAGE_SIZE,
		signal
	);
}

async function fetchAlerts(filters, signal, pageSize = ALERT_PAGE_SIZE) {
	const response = await timesService.alerts(alertQuery(filters, pageSize), signal);
	assertOk(response);
	return listFrom(response);
}

async function fetchEmployeesByIds(ids, signal) {
	if (!ids.length) return [];
	const chunks = [];
	for (let index = 0; index < ids.length; index += 100) {
		chunks.push(ids.slice(index, index + 100));
	}
	const pages = await mapPool(chunks, 4, async (chunk) => {
		const response = await employeesService.getAll(
			{
				variant: "select",
				employee_ids: chunk.join(","),
				page: 0,
				page_size: chunk.length,
			},
			signal
		);
		if (!response || response.ok === false) return [];
		return listFrom(response);
	});
	return pages.flat();
}

async function fetchAssignmentsFor(ids, filters, signal) {
	if (!ids.length) return [];
	const pages = await mapPool(ids, 4, async (id) => {
		try {
			const response = await plansService.getPlanAssignments({
				employee_id: Number(id),
				start: filters.startDate,
				end: filters.endDate,
				page: 0,
				page_size: 100,
				signal,
			});
			if (!response || response.ok === false) return [];
			return listFrom(response);
		} catch (error) {
			if (error?.name === "AbortError") throw error;
			return [];
		}
	});
	return pages.flat();
}

function resetStaff() {
	state.staff = [];
	state.periodMarks = new Map();
	state.censusReady = false;
	state.exhausted = false;
	state.summary = null;
	state.raw = emptyRaw();
	state.loadedShiftIds = new Set();
	state.stickyIds = new Set();
	state.coverage = null;
	state.at = 0;
}

function showSlice() {
	const start = state.page * SUMMARY_PAGE_SIZE;
	const employees = state.staff.slice(start, start + SUMMARY_PAGE_SIZE);
	const period = periodTotalsFromMarks(state.periodMarks);
	state.summary = {
		employees,
		totals: {
			...summarizeEmployees(employees),
			periodStaff: period.staff,
			periodWithAlerts: period.withAlerts,
			periodMissingEmployees: period.missingEmployees,
			periodReady: state.censusReady,
		},
	};
	state.hasNext = summaryHasNextPage(
		state.staff.length,
		state.page,
		SUMMARY_PAGE_SIZE,
		state.exhausted
	);
	state.at = Date.now();
	state.loading = false;
	state.error = null;
	emit();
}

function recompute() {
	const built = buildEmployeeSummary({
		...state.raw,
		employees: state.raw.directory,
		now: moment(),
	});
	state.periodMarks = new Map();
	built.employees.forEach((employee) => {
		state.periodMarks.set(String(employee.id), periodEmployeeMark(employee));
	});
	state.staff = preserveSummaryStaff(
		state.staff,
		built.employees,
		state.stickyIds
	);
	state.censusReady = true;
	state.exhausted = true;
	showSlice();
}

function canServe(page) {
	if (!state.at || Date.now() - state.at >= CACHE_MS) return false;
	const start = page * SUMMARY_PAGE_SIZE;
	if (state.staff.length >= start + SUMMARY_PAGE_SIZE) return true;
	if (!state.exhausted) return false;
	if (state.staff.length === 0) return page === 0;
	return start < state.staff.length;
}

function nestedEmployeeId(item) {
	return String(item?.employee?.id || item?.employee_id || "");
}

function replaceWhere(items, employeeId, readId, next) {
	return (items || []).filter((item) => readId(item) !== employeeId).concat(next || []);
}

async function decorateVisible(filters, signal, serial, key) {
	const visible = state.summary?.employees || [];
	const pending = visible.map((employee) => employee.id).filter((id) => !state.loadedShiftIds.has(id));
	if (!pending.length) return;
	const groups = await fetchAssignmentsFor(pending, filters, signal);
	if (signal.aborted || serial !== requestSerial || state.key !== key) return;
	pending.forEach((id) => state.loadedShiftIds.add(id));
	const byId = new Map(
		state.raw.assignments.map((group) => [nestedEmployeeId(group), group])
	);
	groups.forEach((group) => {
		const id = nestedEmployeeId(group);
		if (id) byId.set(id, group);
	});
	state.raw.assignments = [...byId.values()];
	recompute();
}

async function paintPlannedHours(filters, signal, serial, key) {
	const pending = (state.summary?.employees || []).filter(
		(employee) => employee.plannedHours == null
	);
	if (!pending.length) return;
	const hours = await fetchPlannedHours(
		pending.map((employee) => employee.id),
		filters,
		signal
	);
	if (signal.aborted || serial !== requestSerial || state.key !== key) return;
	const pendingIds = new Set(pending.map((employee) => String(employee.id)));
	state.raw.plannedHours = state.raw.plannedHours
		.filter((row) => !pendingIds.has(String(row.employee?.id || row.employee_id || "")))
		.concat(hours);
	recompute();
}

function registrationsCoveringSpilledAlerts(extra, alerts, startDate, endDate) {
	const wanted = new Set();
	(alerts || []).forEach((alert) => {
		const day = moment(alert?.day).format("YYYY-MM-DD");
		if (!moment(day, "YYYY-MM-DD", true).isValid()) return;
		if (day >= startDate && day <= endDate) return;
		const id = alertEmployeeId(alert);
		if (id) wanted.add(`${id}|${day}`);
	});
	return (extra || []).filter((registration) => {
		const id = String(registration?.employee?.id || registration?.employee_id || "");
		const fromStart = registration?.start
			? moment(registration.start).format("YYYY-MM-DD")
			: "";
		const fromDay = registration?.day ? moment(registration.day).format("YYYY-MM-DD") : "";
		return wanted.has(`${id}|${fromStart}`) || wanted.has(`${id}|${fromDay}`);
	});
}
function mergeRegistrations(current, extra) {
	const map = new Map();
	(current || []).forEach((item) => {
		if (item?.id != null) map.set(String(item.id), item);
	});
	(extra || []).forEach((item) => {
		if (item?.id != null) map.set(String(item.id), item);
	});
	return [...map.values()];
}

function mergeAssignmentGroups(current, extra) {
	const map = new Map();
	const absorb = (group) => {
		const id = nestedEmployeeId(group);
		if (!id) return;
		const previous = map.get(id);
		if (!previous) {
			map.set(id, group);
			return;
		}
		const days = new Map();
		(previous.plans || []).forEach((plan) => days.set(String(plan.day), plan));
		(group.plans || []).forEach((plan) => days.set(String(plan.day), plan));
		map.set(id, { ...previous, plans: [...days.values()] });
	};
	(current || []).forEach(absorb);
	(extra || []).forEach(absorb);
	return [...map.values()];
}

function decorationFilters(filters) {
	return state.coverage || filters;
}

function startDecorations(filters, serial, key) {
	if (decorationController) decorationController.abort();
	const controller = new AbortController();
	decorationController = controller;
	const { signal } = controller;
	return (async () => {
		try {
			await decorateVisible(filters, signal, serial, key);
			if (signal.aborted || serial !== requestSerial || state.key !== key) return;
			await paintPlannedHours(filters, signal, serial, key);
		} catch (error) {
			if (error?.name !== "AbortError") {
				// Las horas y los turnos decoran la tabla. Si fallan, la lista ya está pintada.
			}
		}
	})();
}

async function loadPeriod(filters, signal, serial, key, { quiet = false } = {}) {
	try {
		const stillCurrent = () => serial === requestSerial && !signal.aborted && state.key === key;
		const [firstAlerts, registrations] = await Promise.all([
			fetchAlerts(filters, signal),
			fetchRegistrations(filters, signal),
		]);
		if (!stillCurrent()) return null;
		const alerts =
			registrations.length >= ALERT_PAGE_SIZE
				? await fetchAlerts(filters, signal, registrations.length + 1)
				: firstAlerts;
		if (!stillCurrent()) return null;

		const spilled = spilledAlertRange(alerts, filters.startDate, filters.endDate);
		const coverage = widenSummaryRange(filters, spilled);
		let coveredRegistrations = registrations;
		if (spilled) {
			const extra = await fetchRegistrations(
				{
					...filters,
					startDate: spilled.startDate,
					endDate: spilled.endDate,
				},
				signal
			);
			if (!stillCurrent()) return null;
			coveredRegistrations = mergeRegistrations(
				registrations,
				registrationsCoveringSpilledAlerts(
					extra,
					alerts,
					filters.startDate,
					filters.endDate
				)
			);
		}
		state.coverage = coverage;

		const missingIds = employeeIdsMissingFromRegistrations(alerts, coveredRegistrations);
		const [directory, ubicationAssignments] = await Promise.all([
			fetchEmployeesByIds(missingIds, signal).catch((error) => {
				if (error?.name === "AbortError") throw error;
				return [];
			}),
			filters.ubicationId
				? fetchAssignmentsFor(missingIds, coverage, signal)
				: Promise.resolve([]),
		]);
		if (serial !== requestSerial || signal.aborted || state.key !== key) return null;

		const scoped = scopeSummaryToUbication(
			coveredRegistrations,
			alerts,
			filters.ubicationId,
			ubicationAssignments
		);
		state.raw = {
			registrations: scoped.registrations,
			alerts: scoped.alerts,
			directory,
			assignments: ubicationAssignments,
			plannedHours: [],
		};
		state.loadedShiftIds = new Set(filters.ubicationId ? missingIds : []);
		recompute();
		await startDecorations(coverage, serial, key);
		return state.summary;
	} catch (error) {
		if (serial !== requestSerial || error?.name === "AbortError") return null;
		state.loading = false;
		if (!quiet) {
			state.error = "load";
			state.summary = null;
		}
		emit();
		return null;
	} finally {
		if (inflightKey === key && inflightSerial === serial) {
			inflight = null;
			inflightKey = "";
		}
	}
}

export function loadSummary(filters, { force = false, page = 0, background = false } = {}) {
	const key = cacheKey(filters);
	const queryChanged = state.key !== key;

	if (!queryChanged && !force) {
		state.page = page;
		state.filters = filters;
		if (canServe(page) || state.censusReady) {
			showSlice();
			startDecorations(decorationFilters(filters), requestSerial, key);
			return Promise.resolve(state.summary);
		}
		state.loading = true;
		emit();
		if (inflight) {
			return inflight.then(() => {
				if (state.key !== key) return null;
				showSlice();
				return state.summary;
			});
		}
		return Promise.resolve(state.summary);
	}

	if (activeController) activeController.abort();
	if (decorationController) decorationController.abort();
	if (!background) resetStaff();
	state.key = key;
	state.page = page;
	state.filters = filters;
	if (!background) {
		state.loading = true;
		state.error = null;
		emit();
	}

	const controller = new AbortController();
	activeController = controller;
	const serial = ++requestSerial;
	const promise = loadPeriod(filters, controller.signal, serial, key, {
		quiet: background,
	});
	inflight = promise;
	inflightKey = key;
	inflightSerial = serial;
	return promise;
}

export function refreshSummaryEmployee(employeeId) {
	if (!state.filters) return Promise.resolve(null);
	const key = state.key;
	const id = String(employeeId);
	state.stickyIds.add(id);
	const controller = new AbortController();
	const filters = {
		...state.filters,
		employeeIds: id,
		employeeId: id,
	};
	return Promise.all([
		fetchAlerts(filters, controller.signal, 1000),
		fetchRegistrations(filters, controller.signal),
		fetchAssignmentsFor([id], filters, controller.signal),
		fetchPlannedHours([id], filters, controller.signal),
	]).then(async ([alerts, registrations, assignments, hours]) => {
		if (state.key !== key) return null;
		const spilled = spilledAlertRange(alerts, filters.startDate, filters.endDate);
		const coverage = widenSummaryRange(filters, spilled);
		let coveredRegistrations = registrations;
		let coveredAssignments = assignments;
		if (spilled) {
			const [extraRegistrations, extraAssignments] = await Promise.all([
				fetchRegistrations(
					{
						...filters,
						startDate: spilled.startDate,
						endDate: spilled.endDate,
					},
					controller.signal
				),
				fetchAssignmentsFor([id], coverage, controller.signal),
			]);
			if (state.key !== key) return null;
			coveredRegistrations = mergeRegistrations(
				registrations,
				registrationsCoveringSpilledAlerts(
					extraRegistrations,
					alerts,
					filters.startDate,
					filters.endDate
				)
			);
			coveredAssignments = mergeAssignmentGroups(assignments, extraAssignments);
		}
		const scoped = scopeSummaryToUbication(
			coveredRegistrations,
			alerts,
			state.filters.ubicationId,
			coveredAssignments
		);
		let directory = state.raw.directory;
		if (employeeIdsMissingFromRegistrations(scoped.alerts, scoped.registrations).includes(id)) {
			const people = await fetchEmployeesByIds([id], controller.signal).catch(() => []);
			if (state.key !== key) return null;
			directory = replaceWhere(directory, id, (person) => String(person?.id || ""), people);
		}
		state.raw = {
			...state.raw,
			registrations: replaceWhere(
				state.raw.registrations,
				id,
				nestedEmployeeId,
				scoped.registrations
			),
			alerts: replaceWhere(state.raw.alerts, id, alertEmployeeId, scoped.alerts),
			assignments: replaceWhere(
				state.raw.assignments,
				id,
				nestedEmployeeId,
				coveredAssignments
			),
			directory,
			plannedHours: replaceWhere(state.raw.plannedHours, id, nestedEmployeeId, hours),
		};
		state.loadedShiftIds.add(id);
		recompute();
		return state.summary;
	});
}

export function reloadSummary({ background = false } = {}) {
	if (!state.filters) return Promise.resolve(null);
	if (background) {
		state.staff.forEach((employee) => state.stickyIds.add(String(employee.id)));
	}
	return loadSummary(state.filters, {
		force: true,
		page: state.page,
		background,
	});
}

export function useEmployeeSummary(filters) {
	const key = filters ? cacheKey(filters) : "";
	const [, setVersion] = useState(0);

	const filtersRef = useRef(filters);
	filtersRef.current = filters;

	useEffect(() => subscribe(() => setVersion((version) => version + 1)), []);

	useEffect(() => {
		const current = filtersRef.current;
		if (!current?.startDate || !current?.endDate) return undefined;
		loadSummary(current, { page: 0 });
		return undefined;
	}, [key]);

	const current = Boolean(filters) && state.key === key;
	return {
		loading: current ? state.loading : Boolean(filters),
		error: current ? state.error : null,
		employees: current ? state.summary?.employees || [] : [],
		totals: current ? state.summary?.totals || null : null,
		page: current ? state.page : 0,
		hasNext: current ? state.hasNext : false,
		setPage: (nextPage) => {
			const currentFilters = filtersRef.current;
			if (!currentFilters) return;
			loadSummary(currentFilters, { page: nextPage });
		},
	};
}
